/**
 * UpgradePrompt Component
 * Modal dialog shown when user hits the free tier upload limit (402 response)
 */
import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Spinner from '@/components/ui/Spinner';
import { usePlans, useCreateCheckout, useSubscription, billingKeys } from '@/hooks/useBilling';
import { ContactLink } from '@/components/contact';
import { formatCurrency } from '@/utils/format';
import { isSubscriptionExistsError, type Plan } from '@/types/billing';

export interface UpgradePromptProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Custom title (optional) */
  title?: string;
  /** Custom message (optional) */
  message?: string;
  /** The limit that was reached (optional, for display) */
  limitReached?: string;
}

// Constants from work package
const FREE_TIER_UPLOAD_LIMIT = 10;

/**
 * Upgrade prompt modal shown when user hits upload limit
 */
export default function UpgradePrompt({
  isOpen,
  onClose,
  title = "Upgrade to Continue",
  message,
  limitReached = `${FREE_TIER_UPLOAD_LIMIT} uploads`,
}: UpgradePromptProps) {
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch available plans
  const { data: plans, isLoading: plansLoading } = usePlans();
  const createCheckout = useCreateCheckout();
  // WP-BILLING-19: a household that already has a subscription (past_due,
  // unpaid, or on a plan without this feature) manages it; it is never sold a
  // second one. A 409 SUBSCRIPTION_EXISTS from checkout routes the same way.
  const { data: subscriptionData } = useSubscription();
  const [existsFromCheckout, setExistsFromCheckout] = useState(false);
  const existing = subscriptionData?.subscription ?? null;
  const hasSubscription = existing !== null || existsFromCheckout;

  // Handle plan selection - redirect to Stripe Checkout or handle instant activation
  const handleSelectPlan = useCallback(
    async (plan: Plan) => {
      setError(null);
      setLoadingPlanId(plan.id);

      try {
        const currentUrl = window.location.origin + '/billing';
        const result = await createCheckout.mutateAsync({
          planId: plan.id,
          successUrl: `${currentUrl}?checkout=success`,
          cancelUrl: `${currentUrl}?checkout=canceled`,
        });

        // WP-BILLING-11: Check for FREE_FOREVER instant activation
        if (result.activated) {
          toast.success(result.message || 'Premium access activated!');
          setLoadingPlanId(null);
          // Refresh subscription and entitlements data
          await queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
          await queryClient.invalidateQueries({ queryKey: billingKeys.entitlements() });
          onClose();
          return;
        }

        // Standard Stripe Checkout flow
        if (result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
        } else {
          throw new Error('Checkout session creation failed');
        }
      } catch (err) {
        setLoadingPlanId(null);
        if (isSubscriptionExistsError(err)) {
          setExistsFromCheckout(true);
          await queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
          return;
        }
        const errorMessage = err instanceof Error ? err.message : 'Failed to start checkout';
        setError(errorMessage);
      }
    },
    [createCheckout, queryClient, onClose]
  );

  // Close handler
  const handleClose = useCallback(() => {
    if (!loadingPlanId) {
      setError(null);
      onClose();
    }
  }, [loadingPlanId, onClose]);

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !loadingPlanId) {
        handleClose();
      }
    },
    [handleClose, loadingPlanId]
  );

  if (!isOpen) return null;

  // Get monthly and annual plans
  const monthlyPlan = plans?.find((p) => p.interval === 'monthly');
  const annualPlan = plans?.find((p) => p.interval === 'yearly');

  // Calculate savings for annual
  const annualSavingsPercent = monthlyPlan && annualPlan
    ? Math.round(100 - (annualPlan.amount / (monthlyPlan.amount * 12)) * 100)
    : 0;

  const displayMessage = message ||
    `You've reached the free tier limit of ${limitReached}. Upgrade to Pro for unlimited document uploads and all premium features.`;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-prompt-title"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
          {/* Close button */}
          <button
            type="button"
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
            onClick={handleClose}
            disabled={!!loadingPlanId}
            aria-label="Close dialog"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header with icon */}
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 mb-4">
              <svg
                className="h-6 w-6 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h2
              id="upgrade-prompt-title"
              className="text-xl font-semibold text-gray-900"
            >
              {title}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {displayMessage}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Plan options, or the way to manage the existing subscription */}
          {hasSubscription ? (
            <div className="space-y-3 text-center">
              {existing?.isCouponBased ? (
                <p className="text-sm text-gray-600">
                  Your plan is managed by support.{' '}
                  <ContactLink
                    topic="billing"
                    context="UpgradePrompt"
                    className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                  >
                    Contact support
                  </ContactLink>{' '}
                  to change it.
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    This household already has a subscription. Manage it from Billing.
                  </p>
                  <Link
                    to="/billing"
                    onClick={onClose}
                    className="inline-flex justify-center w-full px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700"
                  >
                    Manage billing
                  </Link>
                </>
              )}
            </div>
          ) : plansLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Monthly Plan */}
              {monthlyPlan && (
                <PlanOption
                  plan={monthlyPlan}
                  isLoading={loadingPlanId === monthlyPlan.id}
                  disabled={!!loadingPlanId}
                  onSelect={() => handleSelectPlan(monthlyPlan)}
                />
              )}

              {/* Annual Plan */}
              {annualPlan && (
                <PlanOption
                  plan={annualPlan}
                  isLoading={loadingPlanId === annualPlan.id}
                  disabled={!!loadingPlanId}
                  onSelect={() => handleSelectPlan(annualPlan)}
                  badge={annualSavingsPercent > 0 ? `Save ${annualSavingsPercent}%` : undefined}
                  recommended
                />
              )}
            </div>
          )}

          {/* Features list */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3">
              What you get with Pro:
            </p>
            <ul className="space-y-2">
              {[
                'Unlimited document uploads',
                'Advanced flow visualizations',
                'Priority support',
                'Data export',
              ].map((feature) => (
                <li key={feature} className="flex items-center text-sm text-gray-600">
                  <svg
                    className="h-4 w-4 text-green-500 mr-2 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleClose}
              className="text-sm text-gray-500 hover:text-gray-700 focus:outline-none focus:underline"
              disabled={!!loadingPlanId}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Plan Option Component
// ============================================================================

interface PlanOptionProps {
  plan: Plan;
  isLoading: boolean;
  disabled: boolean;
  onSelect: () => void;
  badge?: string;
  recommended?: boolean;
}

function PlanOption({
  plan,
  isLoading,
  disabled,
  onSelect,
  badge,
  recommended = false,
}: PlanOptionProps) {
  const priceDisplay = formatCurrency(plan.amount / 100, plan.currency);
  const intervalLabel = plan.interval === 'monthly' ? '/month' : '/year';

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={`
        w-full p-4 rounded-lg border-2 text-left transition-all
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        ${recommended
          ? 'border-primary-500 bg-primary-50 hover:bg-primary-100'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
        }
        ${disabled && !isLoading ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      aria-label={`Select ${plan.name} plan at ${priceDisplay} ${intervalLabel}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{plan.name}</span>
            {badge && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                {badge}
              </span>
            )}
            {recommended && !badge && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                Recommended
              </span>
            )}
          </div>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-xl font-bold text-gray-900">{priceDisplay}</span>
            <span className="text-sm text-gray-500">{intervalLabel}</span>
          </div>
        </div>

        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          <svg
            className={`h-5 w-5 ${recommended ? 'text-primary-600' : 'text-gray-400'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        )}
      </div>

      {plan.trialPeriodDays > 0 && (
        <p className="mt-2 text-xs text-primary-600">
          Includes {plan.trialPeriodDays}-day free trial
        </p>
      )}
    </button>
  );
}
